import React, { useEffect, useRef, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  TooltipItem,
  ChartTypeRegistry,
  registerables,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { DisplaySymbol } from '../../../utils/currency';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { IntlShape } from 'react-intl';
import messages from '../../../locales/messages';
import { ChartJSOrUndefined } from 'react-chartjs-2/dist/types';
import { InputType } from '../../../utils/data';
import { TCOGraphXAxis } from '../../../utils/defaults';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
  ...registerables,
);

interface Props {
  totalCurrentCost: any;
  totalIBMCost: any;
  inputType: InputType;
  intl: IntlShape;
  onChartReady: any;
}

const TCOBarChart: React.FC<Props> = ({
  totalCurrentCost,
  totalIBMCost,
  inputType,
  intl,
  onChartReady,
}: Props) => {
  //const chartRef = useRef(null);
  const chartRef = useRef<ChartJSOrUndefined<'bar'>>(null);
  const totalCurrentCostValue = totalCurrentCost;
  const totalIBMCostValue = totalIBMCost;

  const [legendPosition, setLegendPosition] = React.useState<'center' | 'end'>(
    'center',
  );
  ChartJS.defaults.borderColor = '#666666';
  ChartJS.defaults.color = '#000000';
  ChartJS.defaults.font.size = 14;
  const accessibilityPlugin = {
    id: 'accessibilityPlugin',
    beforeInit: function (chart: any, options: any) {
      // Set aria-label and role attributes
      const canvas = chart.canvas;
      canvas.setAttribute('aria-label', options.ariaLabel || 'Bar Chart');
      canvas.setAttribute('role', options.role || 'img');
    },
  };

  ChartJS.register(accessibilityPlugin);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < 768,
  );

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1200) {
        setLegendPosition('center');
      } else {
        setLegendPosition('end');
      }
    };

    window.addEventListener('resize', handleResize);

    // Call the function to set the initial state
    handleResize();

    // Cleanup function for the effect
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth < 1024);
    }

    if (typeof window !== 'undefined') {
      handleResize();
    }

    window.addEventListener('resize', handleResize);
    return () => {
      // remove event listener when the component is unmounted to not cause any memory leaks
      // otherwise the event listener will continue to be active
      window.removeEventListener('resize', handleResize);
    };
    // add `isMobile` state variable as a dependency so that
    // it is called every time the window is resized
  }, [isMobile]);

  const { currency } = inputType;
  const options = {
    maintainAspectRatio: false,
    plugins: {
      datalabels: {
        color: '#40BFFF',
        display: true,
        formatter: function (value: string, ctx: any) {
          if (ctx.dataset.data[ctx.dataIndex] > 15000) {
            return ctx.chart.data.datasets[ctx.datasetIndex].label;
          } else {
            return '';
          }
        },
        font: {
          size: isMobile ? 8 : 10,
          style: 'normal' as const,
          family: "'IBM Plex Sans Arabic', sans-serif",
        },
        textAlign: 'center' as const,
      },
      legend: {
        display: true,
        position: 'bottom' as const,
        labels: {
          boxWidth: 10,
          boxHeight: 10,
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context: TooltipItem<keyof ChartTypeRegistry>) {
            const labelText = context.dataset.label || '';
            let label = context.dataset.label || '';

            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label +=
                DisplaySymbol(currency) +
                getChartValue(context.parsed.x, labelText).toLocaleString();
            }
            return label;
          },
        },
        TitleFont: {
          family: "'IBM Plex Sans Arabic', sans-serif",
        },
        bodyFont: {
          family: "'IBM Plex Sans Arabic', sans-serif",
        },
      },
    },
    animation: {
      onComplete: () => {
        if (chartRef.current) {
          const chartInstance = chartRef.current;
          const originalCanvas = chartInstance.canvas as HTMLCanvasElement;
          if (originalCanvas && originalCanvas.height > 0) {
            const width = 1250;
            const height = 1200;
            const scaledCanvas = document.createElement('canvas');
            scaledCanvas.width = width;
            scaledCanvas.height = height;
            const ctx = scaledCanvas.getContext('2d');
            if (ctx) {
              ctx.drawImage(originalCanvas, 0, 0, width, height);
              const imageData = scaledCanvas.toDataURL('image/png', 1.0);
              onChartReady(imageData);
            }
          }
        }
      },
    },

    responsive: true,
    options: {
      plugins: {
        datalabels: {
          color: 'white',
          font: {
            size: 40,
            family: "'IBM Plex Sans Arabic', sans-serif",
          },
          anchor: 'end',
          align: 'end',
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        font: {
          color: 'black',
          family: "'IBM Plex Sans Arabic', sans-serif",
        },
        grid: {
          display: false,
          drawTicks: false,
          drawBorder: false,
        },
        label: {
          display: true,
          font: {
            size: 14,
          },
        },
        ticks: {
          display: true,
          font: {
            size: 14,
            style: 'normal' as const,
            family: "'IBM Plex Sans Arabic', sans-serif",
          },
        },
      },
      y: {
        stacked: true,
        border: {
          color: 'black',
        },
        grid: {
          borderColor: '#666666',
          drawTicks: false,
          display: false,
        },
        title: {
          display: true,
          text: 'Annual Infrastructure TCO',
          font: {
            size: 14,
            family: "'IBM Plex Sans Arabic', sans-serif",
          },
        },
        label: {
          display: false,
        },
        ticks: {
          display: false,
        },
      },
    },
  };

  const findMaxValue = (obj: { [key: string]: number }): number => {
    const values = Object.values(obj);
    return Math.max(...values);
  };

  const setDefaultValues = (
    value: number,
    xAxisType: TCOGraphXAxis = TCOGraphXAxis.Current,
  ): number => {
    if (xAxisType == TCOGraphXAxis.Current) {
      const max = findMaxValue(totalCurrentCost);
      if (value < max / 45) {
        return max / 11;
      } else if (value < max / 35) {
        return max / 7;
      } else if (value < max / 5) {
        return max / 5;
      } else if (value < max / 3) {
        return max / 3;
      }
    } else {
      const max = findMaxValue(totalIBMCost);
      if (value < max / 40) {
        return max / 4;
      } else if (value < max / 4) {
        return max / 3;
      } else if (value < max / 2) {
        return max / 2;
      }
    }
    return value;
  };

  const labels = [
    intl.formatMessage(messages.current),
    intl.formatMessage(messages.with_ibm),
  ];

  const administrationValues = [
    setDefaultValues(totalCurrentCost.totalAnnualSalary),
    setDefaultValues(
      totalIBMCost.totalAnnualAdminCostIBM,
      TCOGraphXAxis.WithIBM,
    ),
  ];
  const supportMaintenanceValues = [
    setDefaultValues(totalCurrentCost.totalMaintenanceSupportCurrent),
    setDefaultValues(
      totalIBMCost.totalMaintenanceSupportIBM,
      TCOGraphXAxis.WithIBM,
    ),
  ];
  const softwareValues = [
    setDefaultValues(totalCurrentCost.totalSoftwareCurrent),
    setDefaultValues(totalIBMCost.totalIBMSoftwareCosts, TCOGraphXAxis.WithIBM),
  ];
  const hardwareValues = [
    setDefaultValues(totalCurrentCost.totalHardwareCurrent),
    setDefaultValues(totalIBMCost.totalHardwareCosts, TCOGraphXAxis.WithIBM),
  ];

  const getChartValue = (
    xAxisType: TCOGraphXAxis,
    textValue: string,
  ): number => {
    if (xAxisType == TCOGraphXAxis.WithIBM) {
      switch (textValue) {
        case intl.formatMessage(messages.hardware): {
          return totalIBMCostValue.totalHardwareCosts;
        }
        case intl.formatMessage(messages.software): {
          return totalIBMCostValue.totalIBMSoftwareCosts;
        }
        case intl.formatMessage(messages.support_Maintenance): {
          return totalIBMCostValue.totalMaintenanceSupportIBM;
        }
        case intl.formatMessage(messages.infrastructure): {
          return totalIBMCostValue.totalAnnualAdminCostIBM;
        }
      }
    } else if (xAxisType == TCOGraphXAxis.Current) {
      switch (textValue) {
        case intl.formatMessage(messages.hardware): {
          return totalCurrentCostValue.totalHardwareCurrent;
        }
        case intl.formatMessage(messages.software): {
          return totalCurrentCostValue.totalSoftwareCurrent;
        }
        case intl.formatMessage(messages.support_Maintenance): {
          return totalCurrentCostValue.totalMaintenanceSupportCurrent;
        }
        case intl.formatMessage(messages.infrastructure): {
          return totalCurrentCostValue.totalAnnualSalary;
        }
      }
    }
    return 0;
  };

  const data = {
    labels: labels,
    datasets: [
      {
        label: intl.formatMessage(messages.hardware),
        data: hardwareValues.map((data) => data),
        backgroundColor: '#198038',
        datalabels: {
          color: 'white',
        },
      },
      {
        label: intl.formatMessage(messages.software),
        data: softwareValues.map((data) => data),
        backgroundColor: '#007D79',
        datalabels: {
          color: 'white',
        },
      },
      {
        label: intl.formatMessage(messages.support_Maintenance),
        data: supportMaintenanceValues.map((data) => data),
        backgroundColor: '#0072C3',
        datalabels: {
          color: 'white',
        },
      },
      {
        label: intl.formatMessage(messages.infrastructure),
        data: administrationValues.map((data) => data),
        backgroundColor: '#0F62FE',
        datalabels: {
          color: 'white',
        },
      },
    ],
  };

  return (
    <div>
      <div className="standard-graph">
        <Bar
          ref={chartRef}
          options={options}
          plugins={[ChartDataLabels]}
          data={data}
        />
      </div>
    </div>
  );
};

export default TCOBarChart;
